import React, { useMemo } from 'react';
import CommonModal from '../../Components/CommonModal';

const GRADE_ORDER = ['A', 'B', 'C', 'D', 'F'];
const GRADE_SCORE_MAP = { A: 100, B: 80, C: 60, D: 40, F: 20 };
const scoreToGradeLetter = (score) => {
    if (score >= 90) return 'A';
    if (score >= 75) return 'B';
    if (score >= 60) return 'C';
    if (score >= 40) return 'D';
    return 'F';
};

// Shared by the main Dashboard's TPCRM widget (ThirdPartyExternalExposure.js)
// and the TPCRM Portal's Analytics page (TpcrmAnalyticsView.js) — both
// compute "Average Grade" the same way now (a weighted average of each
// grade tier's score, same A=100/B=80/C=60/D=40/F=20 convention used
// everywhere else in the app) directly from the vendor grade-count buckets
// each page already has on hand — no extra API call.
function AverageGradeModal({ show, onHide, counts = {} }) {
    const rows = useMemo(() => (
        GRADE_ORDER.map((grade) => {
            const count = Number(counts[grade] || 0);
            const score = GRADE_SCORE_MAP[grade];
            return { grade, count, score, contribution: count * score };
        })
    ), [counts]);

    const totalCount = rows.reduce((sum, r) => sum + r.count, 0);
    const totalContribution = rows.reduce((sum, r) => sum + r.contribution, 0);
    const avgScore = totalCount > 0 ? totalContribution / totalCount : 0;
    const avgGrade = totalCount > 0 ? scoreToGradeLetter(avgScore) : '-';

    return (
        <CommonModal show={show} onHide={onHide} title="Average Grade — How It's Calculated">
            <p style={{ marginTop: 0, marginBottom: 16, color: 'var(--color-text-secondary)', fontSize: 'var(--fs-body-sm)', lineHeight: 1.5 }}>
                Every monitored vendor's letter grade is converted to a score (A=100, B=80, C=60, D=40, F=20),
                averaged across all vendors, then converted back to a letter.
            </p>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-body-sm)' }}>
                <thead>
                    <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                        <th style={{ textAlign: 'left', padding: '8px 6px', color: 'var(--color-text-muted)', fontWeight: 600 }}>Grade</th>
                        <th style={{ textAlign: 'right', padding: '8px 6px', color: 'var(--color-text-muted)', fontWeight: 600 }}>Vendors</th>
                        <th style={{ textAlign: 'right', padding: '8px 6px', color: 'var(--color-text-muted)', fontWeight: 600 }}>Score</th>
                        <th style={{ textAlign: 'right', padding: '8px 6px', color: 'var(--color-text-muted)', fontWeight: 600 }}>Contribution</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.grade} style={{ borderBottom: '1px solid var(--color-border)' }}>
                            <td style={{ padding: '8px 6px', fontWeight: 600, color: 'var(--color-text-primary)' }}>{r.grade}</td>
                            <td style={{ padding: '8px 6px', textAlign: 'right', color: 'var(--color-text-primary)' }}>{r.count}</td>
                            <td style={{ padding: '8px 6px', textAlign: 'right', color: 'var(--color-text-secondary)' }}>{r.score}</td>
                            <td style={{ padding: '8px 6px', textAlign: 'right', color: 'var(--color-text-secondary)' }}>{r.contribution}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div
                style={{
                    marginTop: 16,
                    paddingTop: 12,
                    borderTop: '2px solid var(--color-border-strong)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    fontSize: 'var(--fs-body-sm)',
                    color: 'var(--color-text-secondary)',
                }}
            >
                <span>{totalCount} vendors · total score {totalContribution}</span>
                <span style={{ fontWeight: 700, color: 'var(--color-text-primary)' }}>
                    Average: {avgScore.toFixed(1)} → Grade {avgGrade}
                </span>
            </div>
        </CommonModal>
    );
}

export default AverageGradeModal;
